/**
 * Requêtes CRM (M5 suite ERP) — prospects, opportunités, activités
 * Spec: docs/superpowers/specs/2026-07-11-erp-suite-roadmap.md
 *
 * Contrats backend (packages/api/src/routes/crm/crm.routes.ts, préfixe /api/crm):
 * - GET    /crm/leads                  → { leads, pagination }
 * - GET    /crm/leads/:id              → { lead }
 * - POST   /crm/leads                  → { lead }
 * - PUT    /crm/leads/:id              → { lead }
 * - DELETE /crm/leads/:id              → { deleted }
 * - POST   /crm/leads/:id/convert      → { lead, client } (transaction)
 * - GET    /crm/opportunities          → { opportunities, pagination }
 * - GET    /crm/opportunities/pipeline → { pipeline } (Kanban 5 étapes)
 * - GET    /crm/opportunities/:id      → { opportunity }
 * - POST   /crm/opportunities          → { opportunity }
 * - PUT    /crm/opportunities/:id      → { opportunity }
 * - DELETE /crm/opportunities/:id      → { deleted }
 * - GET    /crm/activities             → { activities, pagination }
 * - GET    /crm/activities/overdue     → { activities } (relances)
 * - POST   /crm/activities             → { activity }
 * - PUT    /crm/activities/:id         → { activity }
 * - DELETE /crm/activities/:id         → { deleted }
 * - POST   /crm/activities/:id/done    → { activity } (idempotent)
 * - GET    /crm/contacts?clientId|leadId → { contacts }
 * - POST   /crm/contacts               → { contact } (clientId XOR leadId)
 * - PUT    /crm/contacts/:id           → { contact }
 * - DELETE /crm/contacts/:id           → { deleted }
 *
 * PIÈGE Prisma Decimal: `Opportunity.amount` sérialise en string JSON —
 * toujours convertir via Number() avant toute arithmétique (cf. toAmount).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { clientKeys, type Client } from '@/api/queries/clients';
import type { DecimalString } from '@efacture/web-billing/lib/decimal';

// ─────────────────────────────────────────────────────────────
// Types — Prospects (Leads)
// ─────────────────────────────────────────────────────────────

export const LEAD_STATUSES = [
  'NEW',
  'CONTACTED',
  'QUALIFIED',
  'CONVERTED',
  'LOST',
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** Statuts terminaux: plus aucune transition ensuite (miroir backend). */
export const TERMINAL_LEAD_STATUSES: readonly LeadStatus[] = [
  'CONVERTED',
  'LOST',
];

export interface Lead {
  id: string;
  name: string;
  company: string | null;
  phone: string | null;
  email: string | null;
  source: string | null;
  status: LeadStatus;
  assignedToId: string | null;
  convertedClientId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadListParams {
  page?: number | undefined;
  limit?: number | undefined;
  search?: string | undefined;
  status?: LeadStatus | undefined;
  assignedToId?: string | undefined;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface LeadListResponse {
  leads: Lead[];
  pagination: PaginationInfo;
}

export interface CreateLeadInput {
  name: string;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  source?: string | null;
  assignedToId?: string | null;
  notes?: string | null;
}

export interface UpdateLeadInput {
  name?: string;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  source?: string | null;
  /** CONVERTED ne se pose JAMAIS ici — passer par useConvertLead. */
  status?: LeadStatus;
  assignedToId?: string | null;
  notes?: string | null;
}

/** Données client fournies à la conversion (les manquants sont hérités du prospect). */
export interface ConvertLeadClientData {
  /**
   * An identified client type of the tenant's tables (PM, PP, PC on the DGI
   * floor, §2.35) — inferred when absent: PM with an IFU, PP otherwise.
   */
  type?: string;
  name?: string;
  firstName?: string | null;
  ifu?: string | null;
  rccm?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
}

export interface ConvertLeadInput {
  /** Lier un client existant (exclusif de clientData). */
  clientId?: string;
  /** Créer un client (exclusif de clientId). */
  clientData?: ConvertLeadClientData;
}

export interface ConvertLeadResponse {
  lead: Lead;
  client: Client;
}

// ─────────────────────────────────────────────────────────────
// Types — Opportunités
// ─────────────────────────────────────────────────────────────

export const OPPORTUNITY_STAGES = [
  'PROSPECTION',
  'PROPOSITION',
  'NEGOCIATION',
  'GAGNEE',
  'PERDUE',
] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];

/** Étapes terminales: plus aucune transition ensuite (miroir backend). */
export const TERMINAL_OPPORTUNITY_STAGES: readonly OpportunityStage[] = [
  'GAGNEE',
  'PERDUE',
];

export interface Opportunity {
  id: string;
  title: string;
  clientId: string | null;
  leadId: string | null;
  /** Prisma Decimal → string en JSON. Convertir via toAmount(). */
  amount: DecimalString | null;
  stage: OpportunityStage;
  probability: number | null;
  expectedCloseDate: string | null;
  assignedToId: string | null;
  lostReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  /** Linked record names, present on pipeline cards (GET /crm/opportunities/pipeline). */
  clientName?: string | null;
  leadName?: string | null;
}

export interface OpportunityListParams {
  page?: number | undefined;
  limit?: number | undefined;
  search?: string | undefined;
  stage?: OpportunityStage | undefined;
  leadId?: string | undefined;
  clientId?: string | undefined;
  assignedToId?: string | undefined;
}

export interface OpportunityListResponse {
  opportunities: Opportunity[];
  pagination: PaginationInfo;
}

export interface CreateOpportunityInput {
  title: string;
  clientId?: string | null;
  leadId?: string | null;
  amount?: number | null;
  stage?: OpportunityStage;
  probability?: number | null;
  /** Date ISO (string) ou null. */
  expectedCloseDate?: string | null;
  assignedToId?: string | null;
  lostReason?: string | null;
  notes?: string | null;
}

export type UpdateOpportunityInput = Partial<CreateOpportunityInput>;

export interface PipelineStage {
  stage: OpportunityStage;
  count: number;
  /** Somme des montants de l'étape (déjà convertie en number côté API). */
  totalAmount: number;
  opportunities: Opportunity[];
}

export interface PipelineResult {
  stages: PipelineStage[];
  totals: {
    count: number;
    totalAmount: number;
  };
}

// ─────────────────────────────────────────────────────────────
// Types — Activités
// ─────────────────────────────────────────────────────────────

export const CRM_ACTIVITY_TYPES = [
  'CALL',
  'MEETING',
  'EMAIL',
  'TASK',
  'NOTE',
] as const;
export type CrmActivityType = (typeof CRM_ACTIVITY_TYPES)[number];

export interface CrmActivity {
  id: string;
  type: CrmActivityType;
  subject: string;
  dueDate: string | null;
  doneAt: string | null;
  leadId: string | null;
  opportunityId: string | null;
  clientId: string | null;
  assignedToId: string | null;
  notes: string | null;
  createdAt: string;
  /** Linked record names, present on list and overdue rows. */
  leadName?: string | null;
  opportunityTitle?: string | null;
  clientName?: string | null;
}

export interface CrmActivityListParams {
  page?: number | undefined;
  limit?: number | undefined;
  type?: CrmActivityType | undefined;
  leadId?: string | undefined;
  opportunityId?: string | undefined;
  clientId?: string | undefined;
  assignedToId?: string | undefined;
  /** true = faites uniquement, false = à faire uniquement. */
  done?: boolean | undefined;
}

export interface CrmActivityListResponse {
  activities: CrmActivity[];
  pagination: PaginationInfo;
}

export interface CreateCrmActivityInput {
  type: CrmActivityType;
  subject: string;
  /** Date ISO (string) ou null. */
  dueDate?: string | null;
  /** Rattachement: AU MOINS un des trois est requis (invariant backend). */
  leadId?: string | null;
  opportunityId?: string | null;
  clientId?: string | null;
  assignedToId?: string | null;
  notes?: string | null;
}

export type UpdateCrmActivityInput = Partial<CreateCrmActivityInput>;

// ─────────────────────────────────────────────────────────────
// Aides
// ─────────────────────────────────────────────────────────────

/** Prisma Decimal (string JSON) → number sûr pour l'arithmétique/l'affichage. */
export { toNum as toAmount } from '@efacture/web-billing/lib/decimal';

/** Une activité est en retard: non faite ET échue. */
export function isActivityOverdue(activity: CrmActivity): boolean {
  return (
    !activity.doneAt &&
    !!activity.dueDate &&
    new Date(activity.dueDate).getTime() < Date.now()
  );
}

// ─────────────────────────────────────────────────────────────
// Clés de requête
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Types — Contacts (personnes rattachées à un client OU un prospect)
// ─────────────────────────────────────────────────────────────

export interface Contact {
  id: string;
  clientId: string | null;
  leadId: string | null;
  name: string;
  role: string | null;
  phones: string[];
  emails: string[];
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

export type ContactParent = { clientId: string } | { leadId: string };

export interface ContactInput {
  name: string;
  role?: string | null | undefined;
  phones?: string[] | undefined;
  emails?: string[] | undefined;
  isPrimary?: boolean | undefined;
}

export const crmKeys = {
  all: ['crm'] as const,
  contacts: () => [...crmKeys.all, 'contacts'] as const,
  contactList: (parent: ContactParent) =>
    [...crmKeys.contacts(), 'list', parent] as const,
  leads: () => [...crmKeys.all, 'leads'] as const,
  leadLists: () => [...crmKeys.leads(), 'list'] as const,
  leadList: (params: LeadListParams) => [...crmKeys.leadLists(), params] as const,
  leadDetails: () => [...crmKeys.leads(), 'detail'] as const,
  leadDetail: (id: string) => [...crmKeys.leadDetails(), id] as const,
  opportunities: () => [...crmKeys.all, 'opportunities'] as const,
  opportunityLists: () => [...crmKeys.opportunities(), 'list'] as const,
  opportunityList: (params: OpportunityListParams) =>
    [...crmKeys.opportunityLists(), params] as const,
  opportunityDetails: () => [...crmKeys.opportunities(), 'detail'] as const,
  opportunityDetail: (id: string) =>
    [...crmKeys.opportunityDetails(), id] as const,
  pipeline: () => [...crmKeys.opportunities(), 'pipeline'] as const,
  activities: () => [...crmKeys.all, 'activities'] as const,
  activityLists: () => [...crmKeys.activities(), 'list'] as const,
  activityList: (params: CrmActivityListParams) =>
    [...crmKeys.activityLists(), params] as const,
  overdueActivities: (assignedToId?: string) =>
    [...crmKeys.activities(), 'overdue', assignedToId ?? 'all'] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API — Prospects
// ─────────────────────────────────────────────────────────────

export async function fetchLeads(params: LeadListParams): Promise<LeadListResponse> {
  return apiClient.get<LeadListResponse>('/crm/leads', {
    page: params.page,
    limit: params.limit,
    search: params.search,
    status: params.status,
    assignedToId: params.assignedToId,
  });
}

async function fetchLead(id: string): Promise<Lead> {
  const { lead } = await apiClient.get<{ lead: Lead }>(`/crm/leads/${id}`);
  return lead;
}

async function createLead(input: CreateLeadInput): Promise<Lead> {
  const { lead } = await apiClient.post<{ lead: Lead }>('/crm/leads', input);
  return lead;
}

async function updateLead(id: string, input: UpdateLeadInput): Promise<Lead> {
  const { lead } = await apiClient.put<{ lead: Lead }>(
    `/crm/leads/${id}`,
    input
  );
  return lead;
}

async function deleteLead(id: string): Promise<void> {
  await apiClient.delete<{ deleted: true }>(`/crm/leads/${id}`);
}

async function convertLead(
  id: string,
  input: ConvertLeadInput
): Promise<ConvertLeadResponse> {
  return apiClient.post<ConvertLeadResponse>(`/crm/leads/${id}/convert`, input);
}

// ─────────────────────────────────────────────────────────────
// Fonctions API — Opportunités
// ─────────────────────────────────────────────────────────────

export async function fetchOpportunities(
  params: OpportunityListParams
): Promise<OpportunityListResponse> {
  return apiClient.get<OpportunityListResponse>('/crm/opportunities', {
    page: params.page,
    limit: params.limit,
    search: params.search,
    stage: params.stage,
    leadId: params.leadId,
    clientId: params.clientId,
    assignedToId: params.assignedToId,
  });
}

async function fetchOpportunity(id: string): Promise<Opportunity> {
  const { opportunity } = await apiClient.get<{ opportunity: Opportunity }>(
    `/crm/opportunities/${id}`
  );
  return opportunity;
}

async function fetchPipeline(): Promise<PipelineResult> {
  const { pipeline } = await apiClient.get<{ pipeline: PipelineResult }>(
    '/crm/opportunities/pipeline'
  );
  return pipeline;
}

async function createOpportunity(
  input: CreateOpportunityInput
): Promise<Opportunity> {
  const { opportunity } = await apiClient.post<{ opportunity: Opportunity }>(
    '/crm/opportunities',
    input
  );
  return opportunity;
}

async function updateOpportunity(
  id: string,
  input: UpdateOpportunityInput
): Promise<Opportunity> {
  const { opportunity } = await apiClient.put<{ opportunity: Opportunity }>(
    `/crm/opportunities/${id}`,
    input
  );
  return opportunity;
}

async function deleteOpportunity(id: string): Promise<void> {
  await apiClient.delete<{ deleted: true }>(`/crm/opportunities/${id}`);
}

// ─────────────────────────────────────────────────────────────
// Fonctions API — Activités
// ─────────────────────────────────────────────────────────────

async function fetchActivities(
  params: CrmActivityListParams
): Promise<CrmActivityListResponse> {
  return apiClient.get<CrmActivityListResponse>('/crm/activities', {
    page: params.page,
    limit: params.limit,
    type: params.type,
    leadId: params.leadId,
    opportunityId: params.opportunityId,
    clientId: params.clientId,
    assignedToId: params.assignedToId,
    done: params.done,
  });
}

async function fetchOverdueActivities(
  assignedToId?: string
): Promise<CrmActivity[]> {
  const { activities } = await apiClient.get<{ activities: CrmActivity[] }>(
    '/crm/activities/overdue',
    { assignedToId }
  );
  return activities;
}

async function createActivity(
  input: CreateCrmActivityInput
): Promise<CrmActivity> {
  const { activity } = await apiClient.post<{ activity: CrmActivity }>(
    '/crm/activities',
    input
  );
  return activity;
}

async function updateActivity(
  id: string,
  input: UpdateCrmActivityInput
): Promise<CrmActivity> {
  const { activity } = await apiClient.put<{ activity: CrmActivity }>(
    `/crm/activities/${id}`,
    input
  );
  return activity;
}

async function deleteActivity(id: string): Promise<void> {
  await apiClient.delete<{ deleted: true }>(`/crm/activities/${id}`);
}

async function markActivityDone(id: string): Promise<CrmActivity> {
  const { activity } = await apiClient.post<{ activity: CrmActivity }>(
    `/crm/activities/${id}/done`
  );
  return activity;
}

// ─────────────────────────────────────────────────────────────
// Hooks — Prospects
// ─────────────────────────────────────────────────────────────

/** Liste paginée des prospects — GET /api/crm/leads */
export function useLeads(
  params: LeadListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: crmKeys.leadList(params),
    queryFn: () => fetchLeads(params),
    staleTime: 1000 * 60, // 1 minute
    enabled: options?.enabled ?? true,
  });
}

/** Détail d'un prospect — GET /api/crm/leads/:id */
export function useLead(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: crmKeys.leadDetail(id),
    queryFn: () => fetchLead(id),
    staleTime: 1000 * 60,
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/** Création d'un prospect — POST /api/crm/leads */
export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLeadInput) => createLead(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.leadLists() });
    },
  });
}

/** Modification d'un prospect — PUT /api/crm/leads/:id */
export function useUpdateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateLeadInput }) =>
      updateLead(id, input),
    onSuccess: (lead) => {
      queryClient.setQueryData(crmKeys.leadDetail(lead.id), lead);
      queryClient.invalidateQueries({ queryKey: crmKeys.leadLists() });
    },
  });
}

/** Suppression d'un prospect — DELETE /api/crm/leads/:id */
export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLead(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: crmKeys.leadDetail(id) });
      queryClient.invalidateQueries({ queryKey: crmKeys.leadLists() });
    },
  });
}

/**
 * Conversion d'un prospect en client — POST /api/crm/leads/:id/convert
 * Invalide aussi les listes de clients (un client peut avoir été créé).
 */
export function useConvertLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ConvertLeadInput }) =>
      convertLead(id, input),
    onSuccess: (result) => {
      queryClient.setQueryData(crmKeys.leadDetail(result.lead.id), result.lead);
      queryClient.invalidateQueries({ queryKey: crmKeys.leadLists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Hooks — Opportunités
// ─────────────────────────────────────────────────────────────

/** Liste paginée des opportunités — GET /api/crm/opportunities */
export function useOpportunities(
  params: OpportunityListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: crmKeys.opportunityList(params),
    queryFn: () => fetchOpportunities(params),
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
  });
}

/** Détail d'une opportunité — GET /api/crm/opportunities/:id */
export function useOpportunity(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: crmKeys.opportunityDetail(id),
    queryFn: () => fetchOpportunity(id),
    staleTime: 1000 * 60,
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/** Pipeline Kanban (5 étapes, colonnes vides comprises) — GET /api/crm/opportunities/pipeline */
export function usePipeline(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: crmKeys.pipeline(),
    queryFn: fetchPipeline,
    staleTime: 1000 * 30, // 30 s — vue de travail
    enabled: options?.enabled ?? true,
  });
}

/** Création d'une opportunité — POST /api/crm/opportunities */
export function useCreateOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOpportunityInput) => createOpportunity(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.opportunityLists() });
      queryClient.invalidateQueries({ queryKey: crmKeys.pipeline() });
    },
  });
}

/** Modification d'une opportunité (étape comprise) — PUT /api/crm/opportunities/:id */
export function useUpdateOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateOpportunityInput;
    }) => updateOpportunity(id, input),
    onSuccess: (opportunity) => {
      queryClient.setQueryData(
        crmKeys.opportunityDetail(opportunity.id),
        opportunity
      );
      queryClient.invalidateQueries({ queryKey: crmKeys.opportunityLists() });
      queryClient.invalidateQueries({ queryKey: crmKeys.pipeline() });
    },
  });
}

/** Suppression d'une opportunité — DELETE /api/crm/opportunities/:id */
export function useDeleteOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteOpportunity(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: crmKeys.opportunityDetail(id) });
      queryClient.invalidateQueries({ queryKey: crmKeys.opportunityLists() });
      queryClient.invalidateQueries({ queryKey: crmKeys.pipeline() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Hooks — Activités
// ─────────────────────────────────────────────────────────────

/** Liste paginée des activités — GET /api/crm/activities */
export function useCrmActivities(
  params: CrmActivityListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: crmKeys.activityList(params),
    queryFn: () => fetchActivities(params),
    staleTime: 1000 * 30,
    enabled: options?.enabled ?? true,
  });
}

/** Relances en retard (non faites + échues) — GET /api/crm/activities/overdue */
export function useOverdueActivities(
  assignedToId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: crmKeys.overdueActivities(assignedToId),
    queryFn: () => fetchOverdueActivities(assignedToId),
    staleTime: 1000 * 30,
    enabled: options?.enabled ?? true,
  });
}

/** Création d'une activité (rattachement requis) — POST /api/crm/activities */
export function useCreateCrmActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCrmActivityInput) => createActivity(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.activities() });
    },
  });
}

/** Modification d'une activité — PUT /api/crm/activities/:id */
export function useUpdateCrmActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateCrmActivityInput;
    }) => updateActivity(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.activities() });
    },
  });
}

/** Suppression d'une activité — DELETE /api/crm/activities/:id */
export function useDeleteCrmActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteActivity(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.activities() });
    },
  });
}

/** Marquer une activité faite (idempotent) — POST /api/crm/activities/:id/done */
export function useMarkActivityDone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markActivityDone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.activities() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Contacts
// ─────────────────────────────────────────────────────────────

/** Contacts d'un client ou d'un prospect — GET /api/crm/contacts */
export function useContacts(parent: ContactParent, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: crmKeys.contactList(parent),
    queryFn: async () => {
      const { contacts } = await apiClient.get<{ contacts: Contact[] }>('/crm/contacts', {
        ...parent,
      });
      return contacts;
    },
    staleTime: 1000 * 30,
    enabled: options?.enabled ?? true,
  });
}

/** Création d'un contact — POST /api/crm/contacts */
export function useCreateContact(parent: ContactParent) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ContactInput) => {
      const { contact } = await apiClient.post<{ contact: Contact }>('/crm/contacts', {
        ...parent,
        ...input,
      });
      return contact;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.contacts() });
    },
  });
}

/** Modification d'un contact — PUT /api/crm/contacts/:id */
export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<ContactInput> }) => {
      const { contact } = await apiClient.put<{ contact: Contact }>(`/crm/contacts/${id}`, input);
      return contact;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.contacts() });
    },
  });
}

/** Suppression d'un contact — DELETE /api/crm/contacts/:id */
export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<{ deleted: true }>(`/crm/contacts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmKeys.contacts() });
    },
  });
}
